/**
 * The plan's needs as a spreadsheet, for talking to vendors.
 *
 * The client's ask, Sept 2026: "generate a spreadsheet with needs documented
 * in the plan, for use in communicating with vendors." So one row per defense
 * and per responsible party, including the defenses nobody has taken yet,
 * because an unmet need is exactly what a vendor call is about.
 *
 * Pure, so the quoting is testable. Everything it formats comes from the
 * helpers the panel already uses, so a measurement never reads one way on
 * screen and another in the file.
 */
import { featureArea, featureLength } from './planGeometry';
import { formatAreaSize, formatLineLength } from './planFeatureFormat';
import { ASSIGNMENT_STATUS_LABEL } from './planAssignmentSummary';
import { effectiveBundleLeader, effectiveBundleTeam } from './planBundles';
import { PLAN_PARTY_TYPE_META, partyDisplayName } from '../components/map/planParties';
import { DEFENSE_STRATEGIES } from '../components/map/defenseStrategies';
import type { DefensePlan, DefensePlanAssignment, DefensePlanFeature, PlanParty } from '../types';

export const NEEDS_CSV_COLUMNS = [
  'Defense',
  'Strategy',
  'Bundle',
  'Kind',
  'Size',
  'Latitude',
  'Longitude',
  'Address',
  'Who',
  // "Yes" on the bundle leader's rows within their bundle (Oct 2026).
  'Leader',
  'Type',
  'Status',
  'What they will do',
  'Plan',
  'Exported',
] as const;

/**
 * RFC 4180 with one addition: a text field starting `=`, `+`, `-` or `@` is
 * prefixed with an apostrophe. Excel and Sheets treat those as formulas, and
 * a party named "+Ridge Vendors" should not execute in somebody's
 * spreadsheet.
 *
 * A number is never a formula and skips the guard. The guard fired on the
 * minus of a western longitude and wrote `'-116.794739`, so the Longitude
 * column opened as text while Latitude next to it opened as a number.
 */
export function csvField(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '""';
  if (typeof value === 'number') return Number.isFinite(value) ? `"${value}"` : '""';
  const guarded = /^[=+\-@]/.test(value) ? `'${value}` : value;
  return `"${guarded.replace(/"/g, '""')}"`;
}

function csvRow(cells: (string | number | null | undefined)[]): string {
  return cells.map(csvField).join(',');
}

/** Centroid of a feature, the same point the panel focuses when a row is clicked. */
export function featureCenter(feature: DefensePlanFeature): { lat: number; lng: number } | null {
  if (!feature.coords?.length) return null;
  const lat = feature.coords.reduce((sum, c) => sum + c.lat, 0) / feature.coords.length;
  const lng = feature.coords.reduce((sum, c) => sum + c.lng, 0) / feature.coords.length;
  return { lat, lng };
}

function sizeOf(feature: DefensePlanFeature): string {
  if (feature.kind === 'line') return formatLineLength(featureLength(feature.coords));
  if (feature.kind === 'area') return formatAreaSize(featureArea(feature.coords));
  return '';
}

/** A feature names its bundle, not the other way round (`feature.bundleId`). */
function bundleOf(plan: DefensePlan, feature: DefensePlanFeature): string {
  if (!feature.bundleId) return '';
  return (plan.bundles ?? []).find((bundle) => bundle.id === feature.bundleId)?.name ?? '';
}

function strategyLabel(strategy: DefensePlanFeature['strategy']): string {
  return DEFENSE_STRATEGIES[strategy]?.label ?? strategy;
}

export function planNeedsFilename(plan: DefensePlan, today = new Date()): string {
  const slug = (plan.title || 'plan')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-+|-+$)/g, '');
  return `REDI-needs-${slug}-${today.toISOString().slice(0, 10)}.csv`;
}

export function buildPlanNeedsCsv({
  plan,
  parties,
  assignments,
  now = new Date(),
}: {
  plan: DefensePlan;
  parties: PlanParty[];
  assignments: DefensePlanAssignment[];
  now?: Date;
}): string {
  const partyById = new Map(parties.map((party) => [party.id, party]));
  const exportedAt = now.toISOString().slice(0, 10);
  const rows: string[] = [csvRow([...NEEDS_CSV_COLUMNS])];

  // Each bundle's leader, resolved once. The team is stored or derived, the
  // same way the bundle row on screen reads it, so the file and the panel
  // agree on who leads.
  const leaderByBundle = new Map<string, string>();
  const assignmentsById = Object.fromEntries(assignments.map((a) => [a.id, a]));
  for (const bundle of plan.bundles ?? []) {
    const members = plan.features.filter((f) => f.bundleId === bundle.id);
    const team = effectiveBundleTeam(bundle, members, assignmentsById);
    const leader = effectiveBundleLeader(bundle, team);
    if (leader) leaderByBundle.set(bundle.id, leader);
  }

  for (const feature of plan.features) {
    const center = featureCenter(feature);
    const base = [
      feature.note || strategyLabel(feature.strategy),
      strategyLabel(feature.strategy),
      bundleOf(plan, feature),
      feature.kind,
      sizeOf(feature),
      // Numbers, not text: six decimals is about 10cm, and csvField only
      // guards a string, so a western longitude stays a longitude.
      center ? Number(center.lat.toFixed(6)) : '',
      center ? Number(center.lng.toFixed(6)) : '',
      feature.address ?? '',
    ];

    const links = assignments.filter((a) => a.featureId === feature.id);
    if (links.length === 0) {
      // A defense with nobody on it still gets a row. It is the most useful
      // line in the file: it is the work that has not been picked up.
      rows.push(csvRow([...base, '', '', '', 'Not assigned yet', '', plan.title, exportedAt]));
      continue;
    }
    for (const link of links) {
      const party = partyById.get(link.partyId);
      rows.push(
        csvRow([
          ...base,
          party ? partyDisplayName(party) : '',
          feature.bundleId && leaderByBundle.get(feature.bundleId) === link.partyId ? 'Yes' : '',
          party ? (PLAN_PARTY_TYPE_META[party.type]?.label ?? party.type) : '',
          ASSIGNMENT_STATUS_LABEL[link.status] ?? link.status,
          link.description ?? '',
          plan.title,
          exportedAt,
        ])
      );
    }
  }

  // Excel wants CRLF; everything else tolerates it.
  return `${rows.join('\r\n')}\r\n`;
}
